import type { FormInstance } from 'antd';
import type { DoctorAisLinkInput } from '../../../graphql/generated/graphql.ts';

type Toast = { success: (text: string) => unknown; error: (text: string) => unknown };

type SaveSteps<T> = {
  saveDoctor: () => Promise<T>;
  saveLinks: (doctorId: string, links: DoctorAisLinkInput[]) => Promise<unknown>;
  message: Toast;
  close: () => void;
};

const trimmedLinks = (links: DoctorAisLinkInput[] | undefined): DoctorAisLinkInput[] =>
  (links ?? []).map(l => ({ vendor: l.vendor, externalId: l.externalId.trim() }));

const linkKeys = (links: DoctorAisLinkInput[]): string[] =>
  trimmedLinks(links)
    .map(l => JSON.stringify([l.vendor, l.externalId]))
    .sort();

// setDoctorAisLinks replaces every link, so an unchanged set is not sent
const linksChanged = (links: DoctorAisLinkInput[], loaded: DoctorAisLinkInput[]): boolean =>
  linkKeys(links).join() !== linkKeys(loaded).join();

const errorMessage = (e: unknown): string => (e instanceof Error ? e.message : String(e));

// Attach the server message to the row whose Kennung it names; without a match only the toast remains
const markAisLinkError = (form: Pick<FormInstance, 'setFields'>, links: DoctorAisLinkInput[], message: string) => {
  const index = links.findIndex(l => l.externalId && message.includes(l.externalId));
  if (index >= 0) form.setFields([{ name: ['aisLinks', index, 'externalId'], errors: [message] }]);
};

// The dialog closes even when the links fail: a retry would create the Zuweiser twice
export const createDoctorWithAisLinks = async (
  aisLinks: DoctorAisLinkInput[] | undefined,
  { saveDoctor, saveLinks, message, close }: SaveSteps<string | undefined>
) => {
  let doctorId: string | undefined;
  try {
    doctorId = await saveDoctor();
  } catch (e) {
    message.error(`Zuweiser konnte nicht erstellt werden: ${e}`);
    return;
  }

  const links = trimmedLinks(aisLinks);
  if (doctorId && links.length > 0) {
    try {
      await saveLinks(doctorId, links);
    } catch (e) {
      message.error(
        `Zuweiser wurde erstellt, die AIS-Verknüpfungen konnten nicht gespeichert werden: ${errorMessage(e)}. Bitte im Bearbeiten-Dialog erneut speichern.`
      );
      close();
      return;
    }
  }

  message.success('Zuweiser wurde erstellt');
  close();
};

export const updateDoctorWithAisLinks = async (
  doctorId: string,
  aisLinks: DoctorAisLinkInput[] | undefined,
  loadedLinks: DoctorAisLinkInput[],
  { saveDoctor, saveLinks, message, close, form }: SaveSteps<unknown> & { form: Pick<FormInstance, 'setFields'> }
) => {
  try {
    await saveDoctor();
  } catch (e) {
    message.error(`Zuweiser konnte nicht aktualisiert werden: ${e}`);
    return;
  }

  const links = trimmedLinks(aisLinks);
  if (linksChanged(links, loadedLinks)) {
    try {
      await saveLinks(doctorId, links);
    } catch (e) {
      const text = errorMessage(e);
      markAisLinkError(form, links, text);
      message.error(`Zuweiser gespeichert, die AIS-Verknüpfungen konnten nicht gespeichert werden: ${text}`);
      return;
    }
  }

  message.success('Zuweiser wurde aktualisiert');
  close();
};
